import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

function createAuthHlsLoader(authorizationToken) {
  return class AuthLoader extends Hls.DefaultConfig.loader {
    load(context, config, callbacks) {
      if (authorizationToken && context?.url) {
        try {
          const u = new URL(context.url);
          if (!u.searchParams.get("Authorization")) {
            u.searchParams.set("Authorization", authorizationToken);
            context.url = u.toString();
          }
        } catch (_) {
          /* keep original */
        }
      }
      super.load(context, config, callbacks);
    }
  };
}

/**
 * Renders program media from API `playback` object.
 * Falls back to legacy `url` / YouTube detection when playback is absent.
 */
export default function ProgramVideoPlayer({
  playback,
  fallbackUrl = "",
  title = "Video",
  width = "100%",
  height = 315,
  className = "",
  videoClassName = "",
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const effective = playback || null;
  const type = effective?.type;
  const url = effective?.url || fallbackUrl || "";

  useEffect(() => {
    const video = videoRef.current;
    if (!video || type !== "hls" || !url) return undefined;

    const destroy = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    destroy();

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        loader: createAuthHlsLoader(effective?.authorizationToken),
      });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        console.error("HLS error", data);
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
    }

    return destroy;
  }, [type, url, effective?.authorizationToken]);

  if (type === "processing") {
    return (
      <div className={className} style={{ padding: 12, opacity: 0.8 }}>
        Video is processing. Please try again shortly.
      </div>
    );
  }

  if (type === "image" && url) {
    return (
      <div className={className}>
        <img src={url} alt={title} style={{ width, maxHeight: height, objectFit: "contain" }} />
      </div>
    );
  }

  if (type === "download" && url) {
    return (
      <div className={className}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          Download file
        </a>
      </div>
    );
  }

  if (!url && type !== "hls" && type !== "processing") {
    return null;
  }

  if (type === "iframe" || (!type && (url.includes("youtube.com/embed") || url.includes("player.vimeo.com")))) {
    return (
      <div className={className}>
        <iframe
          width={width}
          height={height}
          src={url}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  // raw | hls | legacy file URL
  return (
    <div className={className}>
      <video
        ref={videoRef}
        className={videoClassName}
        width={width}
        height={height}
        controls
        preload="metadata"
        crossOrigin="anonymous"
        src={type === "hls" ? undefined : url}
      >
        {type !== "hls" ? <source src={url} /> : null}
        Your browser does not support the video tag.
      </video>
    </div>
  );
}
